import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaLaptopCode, FaTools, FaHeartbeat, FaConciergeBell, FaBriefcase, FaHeadset, FaLeaf, FaUtensils } from 'react-icons/fa';

const YEAR = new Date().getFullYear();

// ─── Specialty lists — each section sorted alphabetically by label ─────────────

const AGRICULTURE = [
  { slug: 'agricultural-technician', label: 'Agricultural Technician',    desc: 'Agronomists, Extension Officers & Farm Managers' },
  { slug: 'farm-worker',             label: 'Farm Worker',                 desc: 'Crop, Livestock & Seasonal Agricultural Labour' },
];

const BPO = [
  { slug: 'call-centre',       label: 'Call Centre Agent',                  desc: 'Inbound, Outbound & Contact Centre Roles' },
  { slug: 'customer-service',  label: 'Customer Service Representative',    desc: 'Retail, Banking, BPO & Corporate Customer Care' },
  { slug: 'technical-support', label: 'Technical Support',                  desc: 'IT Help Desk, Desktop & Tier 1/2 Support' },
];

const HEALTHCARE = [
  { slug: 'caregiver',           label: 'Caregiver / Healthcare Assistant', desc: 'Home Care, Nursing Assistants & Care Aides' },
  { slug: 'dental',             label: 'Dentist / Dental Assistant',        desc: 'Dentists, Dental Therapists & Hygienists' },
  { slug: 'medical-technologist',label: 'Medical Technologist',             desc: 'Lab Scientists, Pathology & Diagnostic Tech' },
  { slug: 'nurse',              label: 'Nurse / Nursing',                   desc: 'Registered Nurses, Enrolled Nurses & Midwives' },
  { slug: 'pharmacist',         label: 'Pharmacist',                        desc: 'Retail, Hospital & Clinical Pharmacy' },
  { slug: 'physiotherapist',    label: 'Physiotherapist',                   desc: 'Rehab, Sports & Physical Therapy' },
  { slug: 'radiographer',       label: 'Radiographer',                      desc: 'X-Ray, CT, MRI & Diagnostic Imaging' },
];

const HOSPITALITY = [
  { slug: 'bartender',    label: 'Bartender',          desc: 'Hotels, Resorts & Entertainment Venues' },
  { slug: 'events',       label: 'Events Coordinator', desc: 'Weddings, Corporate Events & Conferences' },
  { slug: 'hotel-resort', label: 'Hotel / Resort Worker', desc: 'Front Desk, Concierge & Guest Services' },
  { slug: 'housekeeper',  label: 'Housekeeper',        desc: 'Hotels, Resorts & Private Households' },
  { slug: 'tour-guide',   label: 'Tour Guide',         desc: 'Licensed Guides, Excursions & Cultural Tours' },
];

const PROFESSIONAL = [
  { slug: 'accounting',      label: 'Accountant / Bookkeeper',    desc: 'Finance Officers, Auditors & Bookkeepers' },
  { slug: 'administrative',  label: 'Administrative Assistant',   desc: 'Receptionists, Secretaries & Office Admins' },
  { slug: 'data-entry',      label: 'Data Entry Clerk',           desc: 'Data Processors & Records Clerks' },
  { slug: 'procurement',     label: 'Procurement Officer',        desc: 'Buyers, Purchasing Officers & Supply Chain' },
  { slug: 'social-work',     label: 'Social Worker',              desc: 'Community Development & Child Welfare' },
];

const TECHNOLOGY = [
  { slug: 'animation-graphic-design', label: 'Animation & Graphic Design',        desc: 'Designers, Animators & Motion Artists' },
  { slug: 'ar-vr',                    label: 'AR / VR',                            desc: 'Augmented & Virtual Reality' },
  { slug: 'artificial-intelligence',  label: 'Artificial Intelligence',            desc: 'AI, ML & Data Science Roles' },
  { slug: 'asset-tracking',           label: 'Asset Tracking (RFID & Barcode)',    desc: 'RFID, Barcode & Inventory Systems' },
  { slug: 'biometrics',               label: 'Biometrics',                         desc: 'Identity Verification & Security' },
  { slug: 'blockchain',               label: 'Blockchain',                         desc: 'Blockchain Developers & DeFi' },
  { slug: 'business-intelligence',    label: 'Business Intelligence & Analytics',  desc: 'BI Developers & Data Analysts' },
  { slug: 'cloud',                    label: 'Cloud Computing',                    desc: 'Cloud Engineers, DevOps & Architects' },
  { slug: 'cms',                      label: 'CMS',                                desc: 'WordPress, Drupal & Headless CMS' },
  { slug: 'crm',                      label: 'CRM',                                desc: 'Salesforce, HubSpot & Zoho' },
  { slug: 'database',                 label: 'Database',                           desc: 'DBAs, Data Engineers & SQL' },
  { slug: 'digital-asset-management', label: 'Digital Asset Management',           desc: 'DAM Admins & Digital Librarians' },
  { slug: 'digital-marketing',        label: 'Digital Marketing',                  desc: 'SEO, Paid Media & Social' },
  { slug: 'digital-signage',          label: 'Digital Signage',                    desc: 'AV & Display Network Specialists' },
  { slug: 'document-management',      label: 'Document Management',                desc: 'Records Managers & DMS Admins' },
  { slug: 'embedded-software',        label: 'Embedded Software',                  desc: 'Embedded & Low-Level Developers' },
  { slug: 'engineering-rd',           label: 'Engineering R&D',                    desc: 'Product & Systems Engineering' },
  { slug: 'erp',                      label: 'ERP',                                desc: 'SAP, Oracle & Dynamics Specialists' },
  { slug: 'facilities-management',    label: 'Facilities Management',              desc: 'CMMS & Building Systems' },
  { slug: 'field-service-management', label: 'Field Service Management',           desc: 'FSM & Field Operations' },
  { slug: 'firmware',                 label: 'Firmware',                           desc: 'Firmware & Hardware Engineering' },
  { slug: 'franchise-management',     label: 'Franchise Management',               desc: 'Multi-Location Operations Tech' },
  { slug: 'fraud-protection',         label: 'Fraud Protection',                   desc: 'Anti-Fraud & Risk Systems' },
  { slug: 'gis',                      label: 'GIS',                                desc: 'Geographic Information Systems' },
  { slug: 'iot',                      label: 'IoT & M2M',                          desc: 'Connected Devices & Smart Systems' },
  { slug: 'it-service-management',    label: 'IT Service Management',              desc: 'ITSM, ITIL & ServiceNow' },
  { slug: 'kiosk',                    label: 'Kiosk',                              desc: 'Self-Service & Touchscreen Tech' },
  { slug: 'legacy',                   label: 'Legacy Systems',                     desc: 'Mainframe, COBOL & AS/400' },
  { slug: 'location-based-services',  label: 'Location Based Services',            desc: 'GPS, Mapping & Geofencing' },
  { slug: 'middleware',               label: 'Middleware',                         desc: 'Integration, APIs & ESB' },
  { slug: 'mobile-tablet',            label: 'Mobile & Tablet',                    desc: 'iOS, Android & Cross-Platform' },
  { slug: 'open-source',              label: 'Open Source',                        desc: 'Linux, Docker & Open Source Dev' },
  { slug: 'pos-payment',              label: 'POS / Payment Processing',           desc: 'POS Systems & Payment Gateways' },
  { slug: 'sales-management',         label: 'Sales Management',                   desc: 'Sales Ops & CRM Tech' },
  { slug: 'solutions',                label: 'Solutions',                          desc: 'IT Solutions Architects & Consultants' },
  { slug: 'telephony',                label: 'Telephony',                          desc: 'VoIP, Contact Centre & Unified Comms' },
  { slug: 'ticketing',                label: 'Ticketing',                          desc: 'Helpdesk & Event Ticketing Platforms' },
  { slug: 'ui-ux',                    label: 'UI / UX',                            desc: 'Product Design & User Experience' },
  { slug: 'wearable-technology',      label: 'Wearable Technology',                desc: 'Smartwatches, Health & IoT Wearables' },
  { slug: 'web-development',          label: 'Web Development',                    desc: 'Frontend, Backend & Full Stack' },
];

const FOOD_RESTAURANT = [
  { slug: 'baker-pastry',        label: 'Baker / Pastry Chef',         desc: 'Pastry Arts, Bread Baking & Confectionery' },
  { slug: 'bartender',           label: 'Bartender',                   desc: 'Hotels, Bars, Resorts & Entertainment Venues' },
  { slug: 'catering',            label: 'Catering Staff',              desc: 'Events, Corporate & Private Catering' },
  { slug: 'chef-cook',           label: 'Chef / Cook',                 desc: 'Hotels, Restaurants & Institutional Kitchens' },
  { slug: 'food-delivery',       label: 'Food Delivery Driver',        desc: 'Restaurant & App-Based Delivery Services' },
  { slug: 'food-safety',         label: 'Food Safety Inspector',       desc: 'Quality Control, HACCP & Health Compliance' },
  { slug: 'kitchen-assistant',   label: 'Kitchen Assistant',           desc: 'Prep Cooks, Dishwashers & Kitchen Porters' },
  { slug: 'restaurant-manager',  label: 'Restaurant Manager',          desc: 'Front-of-House, General & Operations Managers' },
  { slug: 'waiter-waitress',     label: 'Waiter / Waitress',           desc: 'Restaurant, Hotel & Banquet Service Staff' },
];

const TRADES = [
  { slug: 'auto-mechanic',      label: 'Auto Mechanic',           desc: 'Vehicle Repair & Automotive Technicians' },
  { slug: 'barber-hairdresser', label: 'Barber / Hairdresser',    desc: 'Cosmetology & Personal Care' },
  { slug: 'carpenter',          label: 'Carpenter',               desc: 'Construction, Joinery & Cabinet Making' },
  { slug: 'cashier',            label: 'Cashier',                 desc: 'Retail, Supermarket & Restaurant Cashiers' },
  { slug: 'chef-cook',          label: 'Chef / Cook',             desc: 'Hotels, Restaurants & Catering' },
  { slug: 'driver-delivery',    label: 'Driver / Delivery',       desc: 'Logistics, Courier & Transport Drivers' },
  { slug: 'electrician',        label: 'Electrician',             desc: 'Residential, Commercial & Industrial' },
  { slug: 'hvac',               label: 'HVAC Technician',         desc: 'Air Conditioning & Refrigeration' },
  { slug: 'landscaping',        label: 'Landscaping & Gardening', desc: 'Groundskeeping & Horticulture' },
  { slug: 'plumber',            label: 'Plumber',                 desc: 'Plumbing Installation & Repair' },
  { slug: 'retail-sales',       label: 'Retail Sales Associate',  desc: 'Sales, Store & Customer-Facing Roles' },
  { slug: 'security-guard',     label: 'Security Guard',          desc: 'Licensed Security Professionals' },
  { slug: 'teacher',            label: 'Teacher',                 desc: 'Primary, Secondary & Early Childhood' },
  { slug: 'warehouse',          label: 'Warehouse Worker',        desc: 'Logistics, Forklift & Inventory' },
  { slug: 'welder',             label: 'Welder',                  desc: 'MIG, TIG & Structural Welding' },
];

const TOTAL = AGRICULTURE.length + BPO.length + FOOD_RESTAURANT.length + HEALTHCARE.length + HOSPITALITY.length + PROFESSIONAL.length + TECHNOLOGY.length + TRADES.length;

// ─── Section component ────────────────────────────────────────────────────────

const Section = ({ icon, title, specialties }) => (
  <section>
    <div className="flex items-center gap-3 mb-5">
      <div className="bg-blue-950 text-[#cddd3a] rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h2 className="text-lg font-bold text-blue-950">{title}</h2>
        <p className="text-xs text-gray-500">{specialties.length} specialties</p>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {specialties.map(s => (
        <Link
          key={s.slug}
          to={`/jobs/specialty/${s.slug}`}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all group flex items-start gap-3"
        >
          <FaChevronRight size={10} className="text-[#cddd3a] mt-1 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
          <div>
            <p className="text-sm font-semibold text-blue-950 group-hover:text-[#cddd3a] transition-colors leading-snug">{s.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const SpecialtyIndex = () => (
  <>
    <Helmet>
      <title>{`Browse Jobs by Specialty in Jamaica ${YEAR} | HireMeJA`}</title>
      <meta name="description" content={`Browse ${TOTAL} job specialty categories in Jamaica. Find roles in Technology, Healthcare, Hospitality, BPO, Trades, Agriculture, and Professional services on HireMeJA.`} />
      <link rel="canonical" href="https://hiremeja.com/jobs/specialties" />
    </Helmet>

    {/* Hero */}
    <div className="bg-blue-950 text-white py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        <nav className="text-sm text-blue-300 mb-4 flex items-center gap-1 flex-wrap">
          <Link to="/" className="hover:text-white">Home</Link>
          <FaChevronRight size={9} />
          <Link to="/jobs" className="hover:text-white">Jobs</Link>
          <FaChevronRight size={9} />
          <span className="text-white">Browse by Specialty</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#cddd3a] uppercase">Browse Jobs by Specialty</h1>
        <p className="mt-3 text-blue-100 max-w-2xl text-sm leading-relaxed">
          Find jobs in Jamaica by specialty. Browse {TOTAL} categories across Technology, Healthcare,
          Hospitality, Customer Service, Trades, Agriculture, and more.
        </p>
      </div>
    </div>

    {/* Sections */}
    <div className="container mx-auto max-w-5xl px-4 py-10 space-y-12">
      <Section icon={<FaLeaf size={17} />}        title="Agriculture & Farming"           specialties={AGRICULTURE} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaHeadset size={17} />}      title="Customer Service & BPO"          specialties={BPO} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaHeartbeat size={17} />}    title="Healthcare & Medical"             specialties={HEALTHCARE} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaConciergeBell size={17} />}title="Hospitality & Tourism"            specialties={HOSPITALITY} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaUtensils size={17} />}     title="Food & Restaurant"               specialties={FOOD_RESTAURANT} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaBriefcase size={17} />}    title="Professional & Administrative"   specialties={PROFESSIONAL} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaLaptopCode size={17} />}   title="Technology & Digital"            specialties={TECHNOLOGY} />
      <div className="border-t border-gray-200" />
      <Section icon={<FaTools size={17} />}        title="Trades & Services"               specialties={TRADES} />

      <div className="text-center pt-2">
        <Link
          to="/jobs"
          className="inline-block bg-blue-950 text-white px-8 py-3 rounded-md text-sm font-semibold hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
        >
          Browse All Jobs in Jamaica
        </Link>
      </div>
    </div>
  </>
);

export default SpecialtyIndex;
